import React, { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { Selector } from '@/components/ui/inputs/selector';
import { Spinner } from '@/components/ui/loading';
import { toStringArray } from '@/utils';
import type { UseEmployeeDimensionalFilterResult } from '@/hooks/attendance/common/useEmployeeDimensionalFilter';
import type { MarksManagerAttendanceStatusCode } from '@/api/domains/attendance/types/marksManager.types';


const STATUS_CODES: MarksManagerAttendanceStatusCode[] = [
  'complete',
  'justifiedAbsence',
  'absentHours',
  'absent',
  'noScheduleWithMarks',
  'holiday',
];

interface MarksManagerQuickFiltersProps {
  dimensionalFilter: UseEmployeeDimensionalFilterResult;
  attendanceStatusCodes: MarksManagerAttendanceStatusCode[];
  onAttendanceStatusCodesChange: (codes: MarksManagerAttendanceStatusCode[]) => void;
}

// ASIS-5569: filtros rápidos "Colaborador" y "Estado asistencia" de Gestor de Marcas — siempre
// visibles (no requieren abrir "Ver filtros avanzados", que vive aparte en
// MarksManagerAdvancedFiltersToggle). Angostos a propósito: no deben ocupar todo el ancho de la
// fila, dejando espacio para las acciones de la Card.
export const MarksManagerQuickFilters: React.FC<MarksManagerQuickFiltersProps> = ({
  dimensionalFilter,
  attendanceStatusCodes,
  onAttendanceStatusCodesChange,
}) => {
  const { t: translate } = useTranslation();

  const statusOptions = useMemo(
    () => STATUS_CODES.map((code) => ({
      value: code,
      label: translate(`attendance:marksManager.filters.statusOptions.${code}`),
    })),
    [translate],
  );

  const handleStatusChange = useCallback((value: string | string[]) => {
    onAttendanceStatusCodesChange(
      toStringArray(value) as MarksManagerAttendanceStatusCode[],
    );
  }, [onAttendanceStatusCodesChange]);

  return (
    <div className="flex flex-wrap gap-4">
      <div className="relative w-full sm:w-64">
        {dimensionalFilter.isLoadingEmployees && (
          <div className="absolute right-3 top-1/2 z-10 -translate-y-1/2">
            <Spinner size="sm" />
          </div>
        )}
        <Selector
          label={
            dimensionalFilter.isLoadingEmployees
              ? translate('attendance:dimensionalFilter.updating')
              : translate('attendance:dimensionalFilter.employee')
          }
          value={dimensionalFilter.selectedEmployeeIds.map(String)}
          options={dimensionalFilter.catalogOptions.employeeOptions}
          onChange={dimensionalFilter.handlers.onEmployeeSelectionChange}
          onSearchChange={dimensionalFilter.handlers.onEmployeeSearchChange}
          onLoadMore={dimensionalFilter.loadMoreEmployees}
          hasNextPage={dimensionalFilter.employeeHasMore}
          isLoadingMore={dimensionalFilter.isLoadingMoreEmployees}
          multiple
          searchable
          isLoading={dimensionalFilter.isLoadingEmployees}
          disabled={dimensionalFilter.isLoadingEmployees}
          placeholder={translate('attendance:dimensionalFilter.allSelected')}
          data-testid="marks-manager-employee-filter"
        />
      </div>
      <div className="w-full sm:w-64">
        <Selector
          label={translate('attendance:marksManager.filters.attendanceStatus')}
          value={attendanceStatusCodes}
          options={statusOptions}
          onChange={handleStatusChange}
          multiple
          searchable
          placeholder={translate('attendance:dimensionalFilter.allSelected')}
          data-testid="marks-manager-attendance-status-filter"
        />
      </div>
    </div>
  );
};

export default MarksManagerQuickFilters;
